import { useAction, useAtom } from '@reatom/npm-react'
import { useMemo } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Numeric, Skeleton, Tooltip } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { InfoMessage } from '@root/shared/ui-kit'

import { reatomWalletDegenHours } from '../atoms'
import { DegenHours } from '../model'

import type { Address } from '@root/entities/address/Address'
import type { Network } from '@root/entities/network'

/**
 * When this wallet makes its money, by the hour of its own week.
 *
 * A hundred and sixty-eight squares, one per hour, each the average profit a
 * trade made in it. A trader's edge is not spread evenly across a week — the
 * same wallet that is patient on a Tuesday morning chases on a Friday night —
 * and no list of trades shows that.
 *
 * Read the squares against each other, not against zero: the colour is the
 * average per trade scaled to the strongest hour either way, so the best and
 * the worst hour are both fully saturated. An hour with no trades is left
 * blank rather than drawn as zero, because a wallet that lost money at 3am
 * and a wallet that was asleep are not the same wallet.
 */

type Props = {
  walletAddress: Address
  network: Network.Network
}

/** Monday first, as the grid reads; the labels are single letters. */
const DAY_LABELS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'] as const

/** Every third hour gets a label, which is as many as fit without crowding. */
const HOUR_LABELLED = new Set([0, 3, 6, 9, 12, 15, 18, 21])

const hourLabel = (hour: number) => {
  if (hour === 0) {
    return '12a'
  }
  if (hour === 12) {
    return '12p'
  }
  return hour < 12 ? `${hour}a` : `${hour - 12}p`
}

/** "6 AM – 7 AM", the way the tooltip names the hour it covers. */
const hourRange = (hour: number) => {
  const clock = (value: number) => {
    const wrapped = value % 24
    if (wrapped === 0) {
      return '12 AM'
    }
    if (wrapped === 12) {
      return '12 PM'
    }
    return wrapped < 12 ? `${wrapped} AM` : `${wrapped - 12} PM`
  }
  return `${clock(hour)} – ${clock(hour + 1)}`
}

export const WalletProfileDegenHours: React.FC<Props> = ({
  walletAddress,
  network,
}) => {
  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomWalletDegenHours({ walletAddress, network }),
    [walletAddress, network],
  )
  const [state] = useAtom(model)
  const refresh = useAction(model.refresh)

  return (
    <Root>
      {AsyncStateN.matchWaiting(state, {
        Error: () => (
          <InfoMessage intent="error" variant="dt" onRetry={refresh} />
        ),
        Waiting: () => <Skeleton width="100%" height={220} />,
        Success: (hours) => <Grid hours={hours} />,
      })}
    </Root>
  )
}

const Grid: React.FC<{ hours: DegenHours.DegenHours }> = ({ hours }) => {
  const { t } = useTranslation()

  const grid = useMemo(() => DegenHours.byHour(hours), [hours])
  const extreme = useMemo(() => DegenHours.extremeOf(hours.cells), [hours])
  const best = useMemo(() => DegenHours.bestHour(hours.cells), [hours])
  const totals = useMemo(() => DegenHours.totalsOf(hours.cells), [hours])

  // Read once: the same moment for every square, and never during render of
  // a child, which would draw the same tree differently twice.
  const now = useMemo(() => new Date(), [hours])
  const nowHour = now.getHours()
  // getDay is Sunday-first; the grid is Monday-first, as the server counts.
  const nowDay = now.getDay() === 0 ? 7 : now.getDay()

  const bestWeekday = best ? t(`common.weekday.${best.day}` as never) : ''
  const windowLabel = t('wallet-profile.degen-hours.days', {
    days: hours.days,
  })

  if (hours.cells.length === 0) {
    return <InfoMessage intent="empty" variant="dt" />
  }

  return (
    <>
      <Note>{t('wallet-profile.degen-hours.note')}</Note>

      <Scroll>
        <Table>
          <HourLabels>
            <Corner />
            {DegenHours.HOURS.map((hour) => (
              <HourLabel key={hour}>
                {HOUR_LABELLED.has(hour) ? hourLabel(hour) : ''}
              </HourLabel>
            ))}
          </HourLabels>

          {DegenHours.DAYS.map((day) => (
            <Row key={day}>
              <DayLabel>{DAY_LABELS[day - 1]}</DayLabel>
              {DegenHours.HOURS.map((hour) => {
                const cell = DegenHours.cellAt(grid, day, hour)
                if (!cell) {
                  return (
                    <Empty
                      key={hour}
                      data-now={
                        hour === nowHour && day === nowDay ? 'yes' : undefined
                      }
                    />
                  )
                }

                const average = DegenHours.perTrade(cell)
                const rate = Math.round(DegenHours.winRate(cell) * 100)
                const weekday = t(`common.weekday.${day}` as never)
                const when = `${weekday} ${hourRange(hour)}`
                const rated = t('wallet-profile.degen-hours.win-rate', { rate })
                const over = t('wallet-profile.degen-hours.over-trades', {
                  count: cell.trades,
                })

                return (
                  <Tooltip
                    key={hour}
                    content={
                      <TooltipBody>
                        <TooltipTotal
                          data-trend={cell.pnl >= 0 ? 'up' : 'down'}
                        >
                          <Numeric
                            value={cell.pnl}
                            preset="currency-amount-short"
                            currency="$"
                          />
                          {` ${over}`}
                        </TooltipTotal>
                        <TooltipWhen>{`${when} · ${rated}`}</TooltipWhen>
                      </TooltipBody>
                    }
                  >
                    <Square
                      data-trend={average >= 0 ? 'up' : 'down'}
                      data-best={best && cell === best ? 'yes' : undefined}
                      data-now={
                        hour === nowHour && day === nowDay ? 'yes' : undefined
                      }
                      style={
                        {
                          // Inline because it is data, not design: one fill per
                          // square, from the figure the square stands for. It
                          // colours the background alone — dimming the whole
                          // element takes the text with it, and a square nobody
                          // can read is a square that says nothing.
                          '--fill': `${Math.round(
                            (0.12 +
                              DegenHours.intensity(average, extreme) * 0.88) *
                              100,
                          )}%`,
                        } as React.CSSProperties
                      }
                    >
                      <Numeric value={average} preset="currency-amount-short" />
                    </Square>
                  </Tooltip>
                )
              })}
            </Row>
          ))}
        </Table>
      </Scroll>

      <Footer>
        {best ? (
          <Fact>
            {t('wallet-profile.degen-hours.best')}
            <FactValue data-trend={best.pnl >= 0 ? 'up' : 'down'}>
              {`${bestWeekday} ${hourLabel(best.hour)} · `}
              <Numeric
                value={best.pnl}
                preset="currency-amount-short"
                currency="$"
              />
            </FactValue>
          </Fact>
        ) : undefined}
        <Fact>
          {t('wallet-profile.degen-hours.average')}
          <FactValue data-trend={totals.perTrade >= 0 ? 'up' : 'down'}>
            <Numeric
              value={totals.perTrade}
              preset="currency-amount-short"
              currency="$"
            />
            {t('wallet-profile.degen-hours.per-trade')}
          </FactValue>
        </Fact>
        <Spacer />
        <Timezone>{`${hours.timezone} · ${windowLabel}`}</Timezone>
      </Footer>
    </>
  )
}

const Root = styled.div`
  overflow: auto;
  display: grid;
  align-content: start;
  gap: 10px;
`

const Note = styled.p`
  margin: 0;
  padding: 0 2px;

  color: var(--text-9);
  font-size: 10px;
  line-height: 14px;
`

/** The grid keeps its own width on a phone rather than squeezing to mush. */
const Scroll = styled.div`
  overflow-x: auto;
  scrollbar-width: thin;
`

const Table = styled.div`
  display: grid;
  gap: 2px;
  min-width: 620px;
`

const Row = styled.div`
  display: grid;
  grid-template-columns: 14px repeat(24, minmax(0, 1fr));
  gap: 2px;
  align-items: stretch;
`

const HourLabels = styled(Row)`
  color: var(--text-9);
  font-size: 9px;
`

const Corner = styled.span``

const HourLabel = styled.span`
  overflow: visible;
  white-space: nowrap;
`

const DayLabel = styled.span`
  align-self: center;

  color: var(--text-9);
  font-size: 9px;
`

const Square = styled.div`
  display: grid;
  place-items: center;

  height: 26px;
  border-radius: 3px;

  color: var(--text-12);
  font-size: 9px;
  font-variant-numeric: tabular-nums;
  cursor: default;

  &[data-trend='up'] {
    background: color-mix(in srgb, var(--up-9) var(--fill), transparent);
  }

  &[data-trend='down'] {
    background: color-mix(in srgb, var(--down-9) var(--fill), transparent);
  }

  /* The hour it is now, so a reader can place themselves in the week. */
  &[data-now='yes'] {
    box-shadow: inset 0 0 0 1px var(--text-9);
  }

  /* The hour that made the most, named in the footer and findable in the
     grid without counting rows. */
  &[data-best='yes'] {
    outline: 1px solid var(--golden-9);
    outline-offset: -1px;
  }
`

const Empty = styled.div`
  height: 26px;
  border: 1px solid var(--background-3);
  border-radius: 3px;

  /* The hour it is now is marked whether or not the wallet traded in it —
     an empty current hour is exactly the one a reader looks for first. */
  &[data-now='yes'] {
    border-color: var(--text-9);
  }
`

const TooltipBody = styled.div`
  display: grid;
  gap: 2px;
`

const TooltipTotal = styled.span`
  font-weight: 500;

  &[data-trend='up'] {
    color: var(--up-9);
  }

  &[data-trend='down'] {
    color: var(--down-9);
  }
`

const TooltipWhen = styled.span`
  color: var(--text-9);
  font-size: 10px;
`

const Footer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;

  font-size: 10px;
`

const Fact = styled.span`
  display: inline-flex;
  gap: 4px;
  align-items: center;

  padding: 4px 8px;
  border-radius: 999px;
  background: var(--background-2);

  color: var(--text-9);
`

const FactValue = styled.span`
  display: inline-flex;
  gap: 2px;
  align-items: center;

  color: var(--text-11);
  font-variant-numeric: tabular-nums;

  &[data-trend='up'] {
    color: var(--up-9);
  }

  &[data-trend='down'] {
    color: var(--down-9);
  }
`

const Spacer = styled.span`
  flex: 1;
`

const Timezone = styled.span`
  color: var(--text-9);
`
